import { GalleryArtEligibilityNotice } from '../components/gallery/GalleryArtEligibilityNotice';
import React,{useEffect,useState} from 'react';
import {useGalleryAvailability} from '../components/gallery/useGalleryAvailability';
import { Link } from 'react-router-dom';
import { ArrowRight, LogIn } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { GalleryInformation } from '../components/gallery/GalleryInformation';
import { GalleryRotationContent } from '../components/gallery/GalleryRotationContent';


const EMPTY_DATA = {
  applications: { status: 'closed', path: '/members/gallery' },
  visiting_address: '115 Melrose Place, San Antonio, TX 78212',
  policies: null,
  current_rotation: null,
  artists: [],
  works: [],
};


export default function DiBarbieriGallery() {
  const [holiday,setHoliday]=useState(null);
  useEffect(()=>{let active=true;fetch('/api/di-barbieri-gallery/holiday-show').then(r=>r.ok?r.json():null).then(value=>{if(active)setHoliday(value);}).catch(()=>{});return()=>{active=false;};},[]);
  const {data:loadedData,loading,error:loadError,retry}=useGalleryAvailability();
  const data=loadedData || EMPTY_DATA;
  const availabilityKnown=!loading&&!loadError;

  const isOpen = data.applications?.status === 'open';
  const rotation = data.current_rotation;

  return (
    <main className="gallery-page min-h-screen bg-white font-gallery-body" data-testid="di-barbieri-gallery-page">
      <SEOHead
        title="Di Barbieri Gallery"
        description="The Di Barbieri Gallery at the Coppini is a rotating member co-op exhibiting and selling work by Coppini artists."
        canonicalPath="/di-barbieri-gallery"
      />

      <section className="relative isolate overflow-hidden bg-[#0e0a0c]" data-testid="gallery-introduction">
        <img
          src="https://customer-assets-rejwkqb3.emergentagent.net/job_1b1ed93e-afd1-4724-aba6-3023a448c3c9/artifacts/kowcl5ke_DiBarbieriGallery_site.jpg"
          alt="The Di Barbieri Gallery storefront illuminated at dusk, with an illuminated painting, carved relief, and lettered sign reading Di Barbieri Gallery"
          className="absolute inset-0 h-full w-full object-cover"
          data-testid="gallery-hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0e0a0c] via-[#0e0a0c]/55 to-[#0e0a0c]/10" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0e0a0c]/70 via-transparent to-transparent" aria-hidden="true" />
        <div className="absolute inset-y-0 left-0 w-2 bg-[#7c2434]" aria-hidden="true" />

        <div className="relative mx-auto flex min-h-[72vh] max-w-6xl flex-col justify-end gap-10 px-5 pb-12 pt-32 md:min-h-[80vh] md:flex-row md:items-end md:justify-between md:px-8 md:pb-16">
          <div className="max-w-2xl border-l-2 border-[#d9b36c] pl-6">
            <p className="text-xs font-semibold uppercase tracking-wide text-[#d9b36c]">At the Coppini</p>
            <h1 className="mt-4 max-w-xl font-gallery-heading text-4xl text-white drop-shadow-sm sm:text-5xl lg:text-6xl">Di Barbieri Gallery</h1>
            <p className="mt-7 max-w-xl font-gallery-display text-2xl leading-9 text-white/90">
              Two separate ways for Coppini members to exhibit.
            </p>
            <p className="mt-6 max-w-lg text-base leading-7 text-white/70">
              Enter the Member Holiday Show, apply for Ongoing Gallery Rotations, or choose both.
            </p>
          </div>
          <aside
            className="w-full max-w-sm border border-white/15 bg-[#17243d]/60 p-6 backdrop-blur-md md:w-auto"
            data-testid="gallery-application-status"
          >
            <p className="text-xs font-semibold uppercase text-white/60">Ongoing Gallery Rotations</p>
            {loading ? <p className="mt-3 text-base text-white" role="status">Checking application availability…</p> : loadError ? <div role="alert" className="mt-3 text-white"><p>Couldn’t check whether applications are open. Please try again.</p><button type="button" onClick={retry} className="mt-4 min-h-[48px] bg-white px-5 py-3 font-semibold text-[#17243d]">Try again</button></div> : isOpen ? (
              <>
                <p className="mt-2 font-gallery-display text-xl leading-7 text-[#d9b36c]">Applications are open</p>
                <Link
                  to={data.applications?.path || '/members/gallery'}
                  className="mt-6 inline-flex items-center gap-2 bg-white px-5 py-3 text-sm font-semibold text-[#17243d] transition-colors hover:bg-[#d9b36c] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#17243d]"
                  data-testid="gallery-apply-continue-link"
                >
                  Apply for a Gallery Rotation <ArrowRight size={17} />
                </Link>
              </>
            ) : (
              <>
                <p className="mt-2 font-gallery-display text-lg leading-7 text-[#d9b36c]" data-testid="gallery-closed-notice">
                  Applications are not currently available. You can sign in to view your existing gallery applications.
                </p>
                <Link
                  to={data.applications?.path || '/members/gallery'}
                  className="mt-6 inline-flex items-center gap-2 border border-white/40 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-white hover:text-[#17243d] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#17243d]"
                  data-testid="gallery-signin-link"
                >
                  <LogIn size={16} /> Sign in to view your gallery records
                </Link>
              </>
            )}
          </aside>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-12 text-lg leading-8 text-[#17243d]" aria-labelledby="exhibit-choices-heading">
        <h2 id="exhibit-choices-heading" className="font-gallery-heading text-3xl">Two ways to exhibit at the Coppini</h2><GalleryArtEligibilityNotice />
        <p className="mt-4 font-semibold">You may enter the Holiday Show without applying for an ongoing gallery rotation.</p>
        <div className="mt-8 grid gap-8 md:grid-cols-2" data-testid="gallery-two-choices">
          <section className="border-2 border-[#7c2434] bg-white p-6" aria-labelledby="holiday-choice">
            <h3 id="holiday-choice" className="font-gallery-heading text-3xl">December Member Holiday Show</h3>
            <p className="mt-4">All members welcome to submit. Opens December 5. On display through December. $5 per painting.</p>
            <p className="mt-4">The Member Holiday Show opens December 5, 2026, in the new Di Barbieri Gallery at the Coppini. Entry does not enroll or accept you into an ongoing rotation.</p><p className="mt-5 border-2 border-[#7c2434] bg-white p-5 text-lg leading-8"><strong>$100 Best of Show prize.</strong> Additional prizes will also be awarded.</p>
            <p className="mt-4" role="status">Entry deadline: November 21, 2026. Up to three paintings. Delivery: morning of December 4. Reception: December 5, 5–8 p.m. Pickup instructions will be confirmed by staff.</p>
            {holiday?.entries_open ? <Link to="/members/holiday-show" className="mt-6 inline-flex min-h-[48px] items-center bg-[#7c2434] px-5 py-3 font-semibold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4">Enter the Holiday Show</Link> : <p className="mt-4">Online Holiday Show entry is not currently available.</p>}
            <p className="mt-4">Exhibition space is subject to artwork review, presentation and display requirements, and available space. Entering does not guarantee that paintings may remain after December.</p>
          </section>
          <section className="border-2 border-[#17243d] bg-white p-6" aria-labelledby="rotation-choice">
            <h3 id="rotation-choice" className="font-gallery-heading text-3xl">Ongoing Gallery Rotations</h3>
            <p className="mt-4">Apply separately to exhibit in a future rotation. Free to apply. Approval and gallery service requirements apply.</p>
            <p className="mt-4">New applications have no application charge or payment step. Existing payment records and receipts are preserved. Later participation obligations still apply.</p>
            {isOpen && availabilityKnown && <Link to="/members/gallery" className="mt-6 inline-flex min-h-[48px] items-center bg-[#17243d] px-5 py-3 font-semibold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4">Apply for a Gallery Rotation</Link>}
            <p className="mt-4">Artist acceptance, individual artwork approval, assigned gallery space and public publication are separate steps.</p>
          </section>
        </div>
      </section>

      <section className="bg-[#17243d]" data-testid="gallery-purpose-section">
        <div className="mx-auto max-w-6xl px-5 py-14 md:px-8 md:py-20">
          <p className="text-xs font-semibold uppercase text-[#c9a86a]">Gallery purpose</p>
          <h2 className="mt-3 max-w-3xl font-gallery-display text-2xl leading-9 text-white">
            The Di Barbieri Gallery is a space inside the Coppini Academy of Fine Art where member artists exhibit and offer their work for sale in rotating, time-limited presentations.
          </h2>
          <p className="mt-5 max-w-2xl text-sm leading-7 text-[#c3c8d4]">
            Members apply, the jury reviews their work, and scheduled rotations display the selected artwork. Public listings show work only during a gallery rotation.
          </p>
        </div>
      </section>

      <section className="bg-white" data-testid="gallery-current-rotation-section">
        <div className="mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-24">
          <div className="flex flex-col justify-between gap-4 border-b border-[#c8cbd1] pb-6 sm:flex-row sm:items-end">
            <div>
              <p className="text-xs font-semibold uppercase text-[#7c2434]">Current rotation</p>
              <h2 className="mt-3 font-gallery-heading text-3xl text-[#17243d]">{loading ? 'Checking the current rotation' : loadError ? 'Gallery listing' : rotation?.title || 'Between rotations'}</h2>
            </div>
            {rotation?.start_date && <p className="text-sm text-[#5d6470]">{rotation.start_date} — {rotation.end_date}</p>}
          </div>

          {loading && <p className="py-12 text-sm text-[#5d6470]" data-testid="gallery-loading-status">Checking the current rotation…</p>}
          {!loading && loadError && <p className="py-12 text-sm text-[#7c2434]" data-testid="gallery-load-error">The gallery listing is temporarily unavailable.</p>}
          {!loading && !loadError && !rotation && (
            <div className="max-w-2xl py-12" data-testid="gallery-no-published-rotation">
              <p className="font-gallery-display text-2xl text-[#313a4a]">No rotation is currently published.</p>
              <p className="mt-3 text-sm leading-6 text-[#5d6470]">Artist and artwork details will appear here once a rotation is confirmed and on display.</p>
            </div>
          )}
          {!loading && !loadError && (
            <GalleryRotationContent
              rotation={rotation}
              artists={data.artists || []}
              works={data.works || []}
            />
          )}
        </div>
      </section>

      <GalleryInformation address={data.visiting_address} policies={data.policies} applicationsOpen={isOpen} availabilityKnown={availabilityKnown} />
    </main>
  );
}